import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd'
import type { StorePageItem } from '@/lib/api/commerce/store-themes'
import { MdAdd, MdCollectionsBookmark, MdDragIndicator, MdHome, MdShoppingBag, MdViewSidebar, MdVisibilityOff } from 'react-icons/md'

/** Páginas fixas cujas seções moram no conteúdo do tema (além da inicial, que é `null`). */
export type ThemeFixedPageId = 'sidebar' | 'product' | 'collection'

interface PageListProps {
    pages: StorePageItem[]
    /** `null` = página inicial; as fixas pelo nome; número = página institucional. */
    currentPageId: number | ThemeFixedPageId | null
    dirtyPageIds: number[]
    onSelect: (pageId: number | ThemeFixedPageId | null) => void
    onReorder: (pages: StorePageItem[]) => void
    onCreate: () => void
}

/** Páginas da loja: a inicial e a barra lateral (do tema) e as institucionais, que se reordenam arrastando. */
export default function PageList({ pages, currentPageId, dirtyPageIds, onSelect, onReorder, onCreate }: PageListProps) {
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) return
        const next = [...pages]
        const [moved] = next.splice(result.source.index, 1)
        next.splice(result.destination.index, 0, moved)
        onReorder(next)
    }

    const itemClass = (selected: boolean) =>
        `flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${selected ? 'bg-primary/10 font-medium text-primary' : 'hover:bg-base-200'}`

    return (
        <div className="flex flex-col gap-2">
            <span className="px-1 text-xs font-semibold uppercase tracking-wide text-base-content/60">Páginas</span>

            <button type="button" className={itemClass(currentPageId === null)} onClick={() => onSelect(null)}>
                <MdHome className="text-base" />
                Página inicial
            </button>
            <button type="button" className={itemClass(currentPageId === 'sidebar')} onClick={() => onSelect('sidebar')}>
                <MdViewSidebar className="text-base" />
                Barra lateral
            </button>
            <button type="button" className={itemClass(currentPageId === 'product')} onClick={() => onSelect('product')}>
                <MdShoppingBag className="text-base" />
                Página de produto
            </button>
            <button
                type="button"
                className={itemClass(currentPageId === 'collection')}
                onClick={() => onSelect('collection')}
            >
                <MdCollectionsBookmark className="text-base" />
                Página de coleção
            </button>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="store-pages">
                    {(provided) => (
                        <ul ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-0.5">
                            {pages.map((page, index) => (
                                <Draggable key={page.id} draggableId={`page-${page.id}`} index={index}>
                                    {(drag) => (
                                        <li ref={drag.innerRef} {...drag.draggableProps} className="flex items-center gap-1">
                                            <span {...drag.dragHandleProps} className="cursor-grab text-base-content/40" title="Arrastar">
                                                <MdDragIndicator />
                                            </span>
                                            <button
                                                type="button"
                                                className={itemClass(currentPageId === page.id)}
                                                onClick={() => onSelect(page.id)}
                                            >
                                                <span className={`flex-1 truncate ${page.visible ? '' : 'text-base-content/50'}`}>
                                                    {page.title}
                                                </span>
                                                {!page.visible && <MdVisibilityOff className="text-base-content/40" title="Escondida" />}
                                                {(!page.publishedAt || dirtyPageIds.includes(page.id) || page.draft) && (
                                                    <span className="badge badge-warning badge-xs" title="Alterações não publicadas" />
                                                )}
                                            </button>
                                        </li>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </ul>
                    )}
                </Droppable>
            </DragDropContext>

            <button type="button" className="btn btn-sm btn-ghost justify-start" onClick={onCreate}>
                <MdAdd /> Nova página
            </button>
        </div>
    )
}
